import { AssessmentSubmodulesNames } from 'components/ReportsModule/types';
import { snakeCase, sortBy } from 'lodash';
import {
  KeyAssessmentAcceleratorModuleAssignmentStatus,
  KeyAssessmentAndEnrichmentActivityAcceleratorModuleStats,
  KeyClassroomEngagementAssessmentAcceleratorResult,
  KeyClassroomEngagementReportResult,
  KeyClassroomEngagementStudentResult,
  KeyStudentEngagementAssessmentAcceleratorModuleAttemptActivity,
  KeyStudentEngagementAssessmentAcceleratorResult,
  Student
} from 'types/schema-generated-types';

import formatUiDate from 'utilities/format-ui-date';
import { BuildAssessmentTypes } from './keyboarding-report-types';

// todo should take t() funtion for translation
export const moduleNameMap: any = {
  [AssessmentSubmodulesNames.ALL_MODULES]: 'All Modules',
  [AssessmentSubmodulesNames.MODULE_1_MATH]: 'Module 1',
  [AssessmentSubmodulesNames.MODULE_2_MATH_ELA]: 'Module 2',
  [AssessmentSubmodulesNames.MODULE_3_ELA]: 'Module 3'
};

export const buildModuleData = (
  data?: KeyAssessmentAndEnrichmentActivityAcceleratorModuleStats[]
) => {
  const result = data?.map(
    ({ modules, totalSecondsSpent, totalFirstAttemptCount, assignmentStatus }) => {
      const module: any = modules?.[0];
      return {
        status: assignmentStatus || KeyAssessmentAcceleratorModuleAssignmentStatus.NOT_ASSIGNED,
        name: moduleNameMap[module.name],
        secToComplete: totalSecondsSpent,
        questionsAnswered: totalFirstAttemptCount
      };
    }
  );

  return result;
};

export const isAllSelected = (selected: string) => {
  return selected === AssessmentSubmodulesNames.ALL_MODULES;
};

export const mapStudentModuleActivities = (
  activities: KeyStudentEngagementAssessmentAcceleratorModuleAttemptActivity[] = [],
  key: string
) => {
  return (
    activities.map(x => {
      // construct a response or multi part json data
      const jsonMetadataKey = snakeCase(x.activity?.name.split(' - ')[1]);

      const responseData =
        jsonMetadataKey === 'construct_a_response'
          ? x.responseData?.[0].text
          : x.responseData?.[1].text;

      const res = {
        [`${key}_question`]: x.activity?.title,
        [`${key}_timeToAnswer`]: x.secondsSpent,
        [`${key}_noOfTries`]: x.attemptCount,
        ...(x.responseData && { [`${key}_${jsonMetadataKey}`]: responseData }),
        activity: x.activity
      };

      return res;
    }) ?? []
  );
};

export const buildSingleStudentModuleData = (
  reportData: KeyStudentEngagementAssessmentAcceleratorResult & { student: Student },
  module: string
) => {
  const selectedModule = reportData?.modules?.find(m => m.modules?.[0].name.includes(module));

  const former = selectedModule?.attempts?.[1];
  const latest = selectedModule?.attempts?.[0];
  // if theres only 1 attempt
  const allActivities = former
    ? [
        ...mapStudentModuleActivities(former?.activities, 'a1'),
        ...mapStudentModuleActivities(latest?.activities, 'a2')
      ]
    : [...mapStudentModuleActivities(latest?.activities, 'a1')];

  const resolved = allActivities.reduce((acc: any, activity: any) => {
    const result = {
      ...acc,
      ...(activity.a1_question && {
        [activity.a1_question]: {
          ...acc?.[activity.a1_question],
          ...acc?.[activity.a2_question],
          ...activity,
          dateCompleted: former ? former?.dateCompleted : latest?.dateCompleted,
          student: reportData.student
        }
      }),
      ...(activity.a2_question && {
        [activity.a2_question]: {
          ...acc?.[activity.a1_question],
          ...acc?.[activity.a2_question],
          ...activity,
          dateCompleted: latest?.dateCompleted,
          student: reportData.student
        }
      })
    };

    return result;
  }, {});

  return {
    AllResponseData: [],
    selectedModuleData: reportData?.modules?.find(m => m.modules?.[0].name.includes(module)),
    attempt1Completed: former?.dateCompleted
      ? formatUiDate(former?.dateCompleted, 'MM/dd/yyyy')
      : 'N/A',
    attempt2Completed: latest?.dateCompleted
      ? formatUiDate(latest?.dateCompleted, 'MM/dd/yyyy')
      : 'N/A',
    data: Object.values(resolved) as BuildAssessmentTypes[]
  };
};

export const buildSingleClassroomModuleData = (
  classroomReport: KeyClassroomEngagementReportResult,
  module: string
) => {
  const foundModule = classroomReport?.assessmentAccelerator?.modules?.find(
    m => m.modules?.[0].name === module
  );

  const data = foundModule?.activityTypes?.map(at => ({
    questionType: at.activityType?.name ?? 'N/A',
    timeToAnswer: at.averageSecondsSpent,
    noOfTimeCompleted: at.totalCompletedCount,
    avgNoOfTries: at.averageAttemptCount,
    noOfQuestionAnswered: at.totalFirstAttemptCount
  }));

  const students = classroomReport?.students?.reduce((acc: any[], { student, ...s }) => {
    const currentModule = s.assessmentAccelerator?.modules?.find(
      m => m.modules?.[0]?.name === module
    );

    if (currentModule) {
      acc.push({
        studentName: `${student?.firstName} ${student?.lastName}`,
        studentId: student?.id,
        timePerQuestion: currentModule.averageSecondsSpent,
        noOfQuestionsCompleted: currentModule.totalCompletedCount,
        noOfTries: currentModule.averageAttemptCount,
        noOfQuestion: currentModule.totalFirstAttemptCount
      });
    }

    return acc;
  }, []);

  return {
    data: sortBy(data, o => o.questionType),
    students,
    averageAttemptCount: foundModule?.averageAttemptCount,
    averageFirstAttemptCount: foundModule?.averageFirstAttemptCount,
    averageSecondsSpent: foundModule?.averageSecondsSpent //todo
  };
};
export const buildAllModuleAssessmentData = (
  reportData?:
    | KeyStudentEngagementAssessmentAcceleratorResult
    | KeyClassroomEngagementAssessmentAcceleratorResult
) => {
  const nameMap: any = {
    [AssessmentSubmodulesNames.MODULE_1_MATH]: 'm1',
    [AssessmentSubmodulesNames.MODULE_2_MATH_ELA]: 'm2',
    [AssessmentSubmodulesNames.MODULE_3_ELA]: 'm3'
  };

  const defaultData = {
    m1_timePerQuestion: null,
    m1_noOfTries: null,
    m2_timePerQuestion: null,
    m2_noOfTries: null,
    m3_timePerQuestion: null,
    m3_noOfTries: null
  };

  const parseValue = (value: any) => {
    if (value === 0) return value;
    if (!value) return null;
    return Math.round(value);
  };

  const data = reportData?.activityTypes?.reduce((acc: any, at) => {
    at.modules.forEach(m => {
      const prefix = nameMap?.[m?.modules?.[0].name!] ?? '';

      acc = {
        ...acc,
        ...(at.activityType?.name && {
          [at.activityType?.name]: {
            ...defaultData,
            ...acc[at.activityType?.name],
            questionType: at.activityType?.name! ?? '',
            ...(prefix && {
              [`${prefix}_timePerQuestion`]: parseValue(m.averageSecondsSpent),
              [`${prefix}_noOfTries`]: parseValue(m.averageAttemptCount)
            })
          }
        })
      };
    });
    return acc;
  }, {});

  return {
    data: Object.values(data ?? {}),
    selectedModuleData: null,
    attempt1Completed: null,
    attempt2Completed: null
  };
};

export const buildStudentAssessmentHeaderList = (
  reportData: KeyStudentEngagementAssessmentAcceleratorResult,
  t: (value: any, ns?: any) => any,
  isAllSelected?: boolean
) => {
  return [
    {
      title: t('timeToComplete', { ns: 'ReportsHelpCards' }),
      subTitle: '',
      subTitleValue: calcMinutes(reportData?.totalSecondsSpent!), //todo
      helpText: isAllSelected
        ? t('allModulesTimeToCompleteHelp', { ns: 'ReportsHelpCards' })
        : t('singleModuleTimeToCompleteHelp', { ns: 'ReportsHelpCards' })
    },
    {
      title: t('numberOfQuestionsAnswered', { ns: 'ReportsHelpCards' }),
      subTitle: 'questions',
      subTitleValue: reportData?.totalFirstAttemptCount,
      helpText: t('numberOfQuestionsAnsweredHelp', { ns: 'ReportsHelpCards' })
    },
    {
      title: t('averageTriesPerQuestion', { ns: 'ReportsHelpCards' }),
      subTitle: 'tries',
      subTitleValue: reportData?.averageAttemptCount,
      helpText: t('averageTriesPerQuestionHelp', { ns: 'ReportsHelpCards' })
    }
  ];
};
export const mapModuleStudents = (
  students: KeyClassroomEngagementStudentResult[],
  selectedModule: string
) => {
  const result =
    students?.map(s => {
      if (!s.assessmentAccelerator) return s;
      const foundModules = s.assessmentAccelerator?.modules.filter(
        m => m.modules?.[0]?.name === selectedModule
      );
      const modules = foundModules?.length! > 0 ? foundModules : s.assessmentAccelerator?.modules;

      return {
        ...s,
        assessmentAccelerator: {
          ...s.assessmentAccelerator,
          modules,
          ...(foundModules && {
            averageSecondsSpent: modules?.[0]?.averageSecondsSpent,
            averageAttemptCount: modules?.[0]?.averageAttemptCount
          })
        }
      };
    }) ?? [];

  return result;
};
export const buildClassAssessmentHeaderList = (
  assessmentAccelerator: KeyClassroomEngagementAssessmentAcceleratorResult,
  t: any
) => {
  return [
    {
      title: 'Average Time Spent per Module',
      subTitle: '',
      subTitleValue: calcMinutes(assessmentAccelerator?.averageSecondsSpent!),
      helpText: t('averageTimeToCompleteHelp', { ns: 'ReportsHelpCards' })
    },
    {
      title: 'Average No. of Questions Answered in One Try',
      subTitle: 'questions',
      subTitleValue: Math.round(assessmentAccelerator?.averageFirstAttemptCount!),
      helpText: t('averageNumberOfQuestionsAnsweredHelp', { ns: 'ReportsHelpCards' })
    },
    {
      title: 'Average Tries Per Question',
      subTitle: 'tries',
      subTitleValue: Math.round(assessmentAccelerator?.averageAttemptCount!),
      helpText: t('averageTriesPerQuestionHelp', { ns: 'ReportsHelpCards' })
    }
  ];
};

const buildActivity = (
  reportData?:
    | KeyStudentEngagementAssessmentAcceleratorResult
    | KeyClassroomEngagementAssessmentAcceleratorResult
) => {
  // this needs a default map to maintain all 3 modules ( which keeps the list order ), as some modules wont be returned
  const defaultMap = {
    [AssessmentSubmodulesNames.MODULE_1_MATH]: { averageAttemptCount: 0, averageSecondsSpent: 0 },
    [AssessmentSubmodulesNames.MODULE_2_MATH_ELA]: {
      averageAttemptCount: 0,
      averageSecondsSpent: 0
    },
    [AssessmentSubmodulesNames.MODULE_3_ELA]: { averageAttemptCount: 0, averageSecondsSpent: 0 }
  };

  const result = reportData?.activityTypes?.map(({ activityType, modules }) => {
    return {
      name: activityType?.name,
      modules: modules.reduce(
        (acc: any, m: any) => ({
          ...acc,

          [m.modules?.[0].name]: {
            averageAttemptCount: m.averageAttemptCount,
            averageSecondsSpent: m.averageSecondsSpent
          }
        }),
        defaultMap
      )
    };
  });
  return result;
};

const getActivityChartData = (
  reportData:
    | KeyStudentEngagementAssessmentAcceleratorResult
    | KeyClassroomEngagementAssessmentAcceleratorResult,
  moduleName: string
) => {
  const activityTypes = buildActivity(reportData);
  const result = activityTypes?.map(at => {
    return {
      name: at.name,
      averageAttemptCount: at.modules[moduleName].averageAttemptCount,
      averageSecondsSpent: at.modules[moduleName].averageSecondsSpent
    };
  });
  return result;
};
//
const buildSortedActivityTypes = (
  reportData:
    | KeyStudentEngagementAssessmentAcceleratorResult
    | KeyClassroomEngagementAssessmentAcceleratorResult
) => {
  const sortedReportData = {
    ...reportData,
    activityTypes: sortBy(reportData?.activityTypes, o => o.activityType?.name)
  };
  return sortedReportData;
};

export const buildModuleSeries = (
  reportData:
    | KeyStudentEngagementAssessmentAcceleratorResult
    | KeyClassroomEngagementAssessmentAcceleratorResult,
  key: 'name' | 'averageAttemptCount' | 'averageSecondsSpent'
) => {
  const sortedReportData = buildSortedActivityTypes(reportData);

  const result = {
    module1Series:
      getActivityChartData(sortedReportData, AssessmentSubmodulesNames.MODULE_1_MATH)?.map(
        x => x[key]
      ) ?? [],
    module2Series:
      getActivityChartData(sortedReportData, AssessmentSubmodulesNames.MODULE_2_MATH_ELA)?.map(
        x => x[key]
      ) ?? [],
    module3Series:
      getActivityChartData(sortedReportData, AssessmentSubmodulesNames.MODULE_3_ELA)?.map(
        x => x[key]
      ) ?? []
  };

  return result;
};

export const buildModuleLabels: any = (
  reportData: KeyStudentEngagementAssessmentAcceleratorResult
) => {
  const sortedReportData = buildSortedActivityTypes(reportData);
  const activityTypes = buildActivity(sortedReportData);

  return activityTypes?.map(at => at.name) ?? [];
};

export const hasJsonResponse = (item: Record<any, any>) => {
  if (item?.['a1_multi_part']) return true;
  if (item?.['a1_construct_a_response']) return true;
  if (item?.['a2_multi_part']) return true;
  if (item?.['a2_construct_a_response']) return true;
  return false;
};

export const buildAllStudentsClassroomData = (students: KeyClassroomEngagementStudentResult[]) => {
  const result = students?.map(s => {
    const sRes = {
      studentName: `${s.student?.firstName} ${s.student?.lastName}`,
      studentId: s?.student?.id,
      timePerQuestion: s.assessmentAccelerator?.averageSecondsSpent,
      noOfQuestionsCompleted: s.assessmentAccelerator?.totalCompletedCount,
      noOfTries: s.assessmentAccelerator?.averageAttemptCount,
      noOfQuestion: s.assessmentAccelerator?.totalFirstAttemptCount,
      // assessmentAccelerator is null for KEYK to KEY2 licenses all others should give a assignmentStatus
      module1Progress: s.assessmentAccelerator?.modules.find(
        m => m.modules?.[0]?.name === AssessmentSubmodulesNames.MODULE_1_MATH
      )?.assignmentStatus,
      module2Progress: s.assessmentAccelerator?.modules.find(
        m => m.modules?.[0]?.name === AssessmentSubmodulesNames.MODULE_2_MATH_ELA
      )?.assignmentStatus,
      module3Progress: s.assessmentAccelerator?.modules.find(
        m => m.modules?.[0]?.name === AssessmentSubmodulesNames.MODULE_3_ELA
      )?.assignmentStatus
    };

    return sRes;
  });

  return result ?? [];
};

export const calcMinutes = (secs: number) => {
  const minutes = Math.floor(secs / 60);
  const seconds = secs - minutes * 60;
  const minsText = minutes ? `${minutes}m` : '';
  const secondText = seconds ? `${seconds}s` : '';

  if (!minsText && !secondText) {
    return `${seconds}s`;
  }
  return minsText ? `${minsText} ${secondText}` : secondText;
};

export const appendSecondsOrFallback = (value: any) => {
  // if (value === 'N/A') return value;
  // if (value === null) return 'N/A';
  if (!value) return '-';

  return calcMinutes(value);
};

export const getDefaultText = (value: string) => {
  // if (value === 'N/A') return value;
  // if (value === null) return 'N/A';
  if (!value) return '-';
  return value;
};
export const hasSingleModuleData = (module: any) => {
  return (
    module.avgNoOfTries ||
    module.noOfQuestionAnswered ||
    module.noOfTimeCompleted ||
    module.timeToAnswer
  );
};

export const getHasTwoAttempts = (data: any[]) => {
  const result = data?.some((x: any) => {
    return Object.keys(x).includes('a2_timeToAnswer') && Object.keys(x).includes('a1_timeToAnswer');
  });
  return result;
};

export const tableHasData = (data: any[]) => {
  const results = data.some(
    (x: any) =>
      x?.m1_noOfTries ||
      x?.m1_timePerQuestion ||
      x?.m2_noOfTries ||
      x?.m2_timePerQuestion ||
      x?.m3_noOfTries ||
      x?.m3_timePerQuestion ||
      (x.avgNoOfTries && x.noOfQuestionAnswered && x.noOfTimeCompleted && x.timeToAnswer)
  );
  return results;
};

export const hasTableAttemptData = (data: any[]) => {
  const results = data.some(
    (x: any) =>
      x.a1_noOfTries ||
      x.a1_question ||
      x.a1_timeToAnswer ||
      x.a2_noOfTries ||
      x.a2_question ||
      x.a2_timeToAnswer
  );
  return results;
};
